import React, { useEffect, useState } from 'react';
import { MapPin, Globe, Sparkles } from 'lucide-react';
import { useIsland } from '../contexts/IslandContext';
import { useLocation } from 'react-router-dom';
import axios from '../lib/axios';
import { API } from '../App';

const IslandSelectorPopup = () => {
  const { islands, showIslandSelector, setCurrentIsland, isDetecting } = useIsland();
  const location = useLocation();
  const [comingSoonActive, setComingSoonActive] = useState(false);

  // Check if Coming Soon mode is active
  useEffect(() => {
    const checkComingSoon = async () => {
      try {
        const response = await axios.get(`${API}/admin/coming-soon`);
        setComingSoonActive(response.data?.enabled === true);
      } catch (error) {
        setComingSoonActive(false);
      }
    };
    checkComingSoon();
  }, []);

  // Don't show on admin routes - admins need unrestricted access
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  // Don't show on homepage when Coming Soon is active
  const isComingSoonPage = comingSoonActive && location.pathname === '/';

  // Body scroll lock when popup is visible
  useEffect(() => {
    if (showIslandSelector && !isAdminRoute && !isComingSoonPage) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showIslandSelector, isAdminRoute, isComingSoonPage]);
  
  if (!showIslandSelector || isAdminRoute || isComingSoonPage) return null;

  const handleSelectIsland = (islandCode) => {
    setCurrentIsland(islandCode);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-lg bg-card rounded-3xl shadow-2xl overflow-hidden border border-border">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-500 to-emerald-500 p-6 text-white text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
            <Globe className="w-8 h-8" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Welcome to Your Buy Site</h2>
          <p className="text-white/90 text-sm">
            Choose your island to see local listings
          </p>
        </div>

        {/* Island Options */}
        <div className="p-6 space-y-3">
          {isDetecting && (
            <div className="text-center py-4 text-muted-foreground">
              <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2"></div>
              <p className="text-sm">Detecting your location...</p>
            </div>
          )}

          {!isDetecting && islands.map((island) => (
            <button
              key={island.code}
              onClick={() => handleSelectIsland(island.code)}
              className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-border hover:border-primary hover:bg-primary/5 transition-all duration-200 group cursor-pointer"
              data-testid={`island-select-${island.code}`}
            >
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-100 to-emerald-100 dark:from-teal-900/30 dark:to-emerald-900/30 flex items-center justify-center text-2xl">
                {island.flag_emoji || '🏝️'}
              </div>
              <div className="flex-1 text-left">
                <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                  {island.name}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {island.locations?.length || 0} locations
                </p>
              </div>
              <MapPin className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
            </button>
          ))}
        </div>

        {/* Footer Note */}
        <div className="px-6 pb-6">
          <p className="text-xs text-center text-muted-foreground">
            You can change your island anytime from the header
          </p>
        </div>
      </div>
    </div>
  );
};

export default IslandSelectorPopup;
